import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FlaskConical, ShieldCheck, Pill, Link as LinkIcon, Layers } from "lucide-react";
import { products, categoryKeys } from "../data/products";
import { useScrollReveal } from "../hooks/useAnimations";

const iconMap = { antibiotic: Pill, coccidiostat: ShieldCheck, api: FlaskConical, binder: LinkIcon, otherPremix: Layers };

export default function ProductCatalogue({ t, lang }) {
  const [activeCategory, setActiveCategory] = useState("antibiotic");
  const [expandedCards, setExpandedCards] = useState({});
  const [headerRef, headerVisible] = useScrollReveal();
  const [contentRef, contentVisible] = useScrollReveal({ threshold: 0.05 });
  const isEn = lang === "en";
  const activeProducts = products[activeCategory];
  const isSingleProductCategory = activeProducts.length === 1;

  const Icon = iconMap[activeCategory];

  return (
    <section id="products" data-testid="products-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`reveal ${headerVisible ? 'visible' : ''}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[2px] bg-brand-blue" />
            <span className="text-brand-blue text-xs font-semibold tracking-[0.25em] uppercase">
              {t.products.sectionLabel}
            </span>
          </div>
          <h2 data-testid="products-title" className="font-heading font-bold text-4xl sm:text-5xl lg:text-[3.2rem] text-brand-blue mb-3 tracking-tight">
            {t.products.title}
          </h2>
          <p className="text-slate-600 max-w-xl mb-8 text-base">{t.products.subtitle}</p>

          <div className="flex flex-wrap gap-2 mb-10">
            {categoryKeys.map((key) => (
              <button
                key={key}
                data-testid={`filter-${key}`}
                onClick={() => setActiveCategory(key)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all duration-300 hover:scale-[1.02] ${
                  activeCategory === key
                    ? "bg-brand-blue text-white border-brand-blue shadow-md shadow-brand-blue/25"
                    : "bg-white text-slate-600 border-slate-200 hover:border-brand-blue hover:text-brand-blue"
                }`}
              >
                {t.products.categories[key]}
              </button>
            ))}
          </div>
        </div>

        <div ref={contentRef} className={`reveal-scale ${contentVisible ? 'visible' : ''}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-brand-blue flex items-center justify-center">
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-brand-blue text-sm">{t.products.categories[activeCategory]}</h3>
              <p className="text-slate-400 text-[11px]">{activeProducts.length} {lang === "en" ? "products" : "个产品"}</p>
            </div>
            <div className="flex-1 h-px bg-slate-100 ml-3" />
          </div>

          <div className={`grid gap-3 ${isSingleProductCategory ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
            {activeProducts.map((product, i) => (
              (() => {
                const cardKey = `${activeCategory}-${i}`;
                const isExpanded = !!expandedCards[cardKey];
                return (
              <Card
                key={`${activeCategory}-${i}`}
                data-testid={`card-${activeCategory}-${i}`}
                className="bg-card-mint border border-brand-blue/20 hover-lift hover-glow group cursor-default"
              >
                <CardHeader className="pb-2 pt-5 px-5">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base font-semibold text-[#333333] group-hover:text-brand-blue transition-colors duration-300 leading-snug">
                      {lang === "en" ? product.name : product.nameZh}
                    </CardTitle>
                    <div className="w-8 h-8 rounded-lg bg-brand-blue flex items-center justify-center flex-shrink-0 ml-2 transition-colors duration-300">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-5 pt-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-label-muted uppercase tracking-wide">{t.products.specification}</span>
                    <span className="text-xs font-semibold text-brand-blue bg-white/60 px-2.5 py-0.5 rounded-md font-mono">
                      {product.specs}
                    </span>
                  </div>
                  {product.details && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedCards((prev) => ({
                            ...prev,
                            [cardKey]: !isExpanded
                          }))
                        }
                        className="inline-flex items-center rounded-md border border-brand-blue/30 bg-white/70 px-3 py-1.5 text-sm font-bold text-brand-blue transition-colors hover:bg-white hover:border-brand-blue"
                      >
                        {isExpanded
                          ? isEn ? "Hide details" : "收起详情"
                          : isEn ? "View full details" : "查看完整详情"}
                      </button>

                      {isExpanded && (
                        <div className="mt-4 space-y-4 text-sm text-slate-700">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                              {isEn ? "Overview" : "产品概述"}
                            </p>
                            <p className="leading-relaxed">
                              {isEn ? product.details.summary : product.details.summaryZh}
                            </p>
                          </div>

                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                              {isEn ? "Composition" : "成分"}
                            </p>
                            <ul className="space-y-1 list-disc pl-4">
                              {(isEn ? product.details.composition : product.details.compositionZh).map((item, idx) => (
                                <li key={`composition-${idx}`} className="leading-relaxed">
                                  <strong>{item.split(":")[0]}:</strong>
                                  {item.includes(":") ? ` ${item.split(":").slice(1).join(":").trim()}` : ""}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                              {isEn ? "Dosage & Administration" : "用量与使用方法"}
                            </p>
                            <p className="leading-relaxed">
                              {isEn ? product.details.dosage : product.details.dosageZh}
                            </p>
                          </div>

                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                              {isEn ? "Uses" : "用途"}
                            </p>
                            <ul className="space-y-1 list-disc pl-4">
                              {(isEn ? product.details.uses : product.details.usesZh).map((item, idx) => (
                                <li key={`uses-${idx}`} className="leading-relaxed">{item}</li>
                              ))}
                            </ul>
                          </div>

                          <div className="grid gap-2 sm:grid-cols-2">
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                                {isEn ? "Shelf Life" : "保质期"}
                              </p>
                              <p className="font-medium">{isEn ? product.details.shelfLife : product.details.shelfLifeZh}</p>
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                                {isEn ? "Country of Origin" : "原产地"}
                              </p>
                              <p className="font-medium">{isEn ? product.details.countryOfOrigin : product.details.countryOfOriginZh}</p>
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                                {isEn ? "Packaging" : "包装"}
                              </p>
                              <p className="font-medium">{isEn ? product.details.packaging : product.details.packagingZh}</p>
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                                {isEn ? "Precaution" : "注意事项"}
                              </p>
                              <p className="leading-relaxed font-medium">
                                {isEn ? product.details.precaution : product.details.precautionZh}
                              </p>
                            </div>
                          </div>

                          {product.details.exporter && (
                            <div className="rounded-lg border border-brand-blue/20 bg-white/60 p-3">
                              <p className="text-xs font-bold uppercase tracking-wide text-label-muted mb-1">
                                {isEn ? "Exported By" : product.details.exporterZh.title}
                              </p>
                              <p className="font-bold text-[#333333]">{isEn ? product.details.exporter.name : product.details.exporterZh.name}</p>
                              <p className="leading-relaxed">{isEn ? product.details.exporter.address : product.details.exporterZh.address}</p>
                              <p>
                                <strong>{isEn ? "E-Mail:" : "邮箱："}</strong>{" "}
                                {isEn ? product.details.exporter.email : product.details.exporterZh.email}
                              </p>
                              <p>
                                <strong>{isEn ? "Cell:" : "电话："}</strong>{" "}
                                {isEn ? product.details.exporter.phone : product.details.exporterZh.phone}
                              </p>
                              <p>
                                <strong>{isEn ? "Website:" : "网站："}</strong>{" "}
                                <a
                                  href={isEn ? product.details.exporter.website : product.details.exporterZh.website}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-brand-blue font-semibold hover:underline"
                                >
                                  {isEn ? product.details.exporter.website : product.details.exporterZh.website}
                                </a>{" "}
                                <span className="text-slate-500">
                                  {isEn ? product.details.exporter.websiteNote : product.details.exporterZh.websiteNote}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
                );
              })()
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
